import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  HiOutlineServer, HiOutlineRefresh,
  HiOutlineExclamationCircle, HiOutlineCurrencyDollar
} from 'react-icons/hi';
import { safeISOString } from '../../shared/constants';
import { getCloudRunMetrics, getCloudRunTraces, getServiceScaling } from '../../services/api';
import LogKV from '../../shared/LogKV';
import { useTheme } from '../../context/ThemeContext';

function getMonthLabel(offset) {
  const d = new Date();
  d.setMonth(d.getMonth() - offset);
  return d.toLocaleString('default', { month: 'long', year: 'numeric' });
}

const COST_MONTH_OPTIONS = Array.from({ length: 6 }, (_, i) => ({
  value: i,
  label: i === 0 ? `${getMonthLabel(0)} (Current)` : getMonthLabel(i),
}));

function DashboardTab({
  product, dashErrors, dashErrorsLoading, dashCosts, dashCostsLoading,
  dashErrorsLastRefreshed, dashCostsLastRefreshed,
  dashAutoRefresh, setDashAutoRefresh,
  fetchDashErrors, fetchDashCosts,
  costMonth, setCostMonth,
  onSwitchTab, serviceInfoCache,
}) {
  const groupedErrors = useMemo(() => {
    if (!dashErrors.length) return [];
    const groups = {};
    for (const err of dashErrors) {
      const msg = err.textPayload || (err.jsonPayload ? (typeof err.jsonPayload === 'string' ? err.jsonPayload : JSON.stringify(err.jsonPayload)) : '');
      const key = `${err.severity}|${err.resource?.labels?.service_name || ''}|${msg.slice(0, 150)}`;
      if (!groups[key]) {
        groups[key] = { err, message: msg, count: 0, latest: err.timestamp, earliest: err.timestamp };
      }
      groups[key].count++;
      if (new Date(err.timestamp) > new Date(groups[key].latest)) groups[key].latest = err.timestamp;
      if (new Date(err.timestamp) < new Date(groups[key].earliest)) groups[key].earliest = err.timestamp;
    }
    return Object.values(groups).sort((a, b) => new Date(b.latest) - new Date(a.latest));
  }, [dashErrors]);

  const [requestData, setRequestData] = useState(null);
  const [requestDataLoading, setRequestDataLoading] = useState(false);

  const fetchRequestData = useCallback(async () => {
    if (!product?.cloudRuns?.length) return;
    setRequestDataLoading(true);
    try {
      const now = new Date();
      const startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      const endTime = now.toISOString();
      const results = await Promise.allSettled(
        product.cloudRuns.map(svc =>
          getCloudRunMetrics(svc.gcpProjectId, svc.serviceName, svc.region, { startTime, endTime })
        )
      );
      const allPoints = [];
      const instancePoints = [];
      let alignSec = 300;
      const perService = {};
      results.forEach((r, idx) => {
        const svcName = product.cloudRuns[idx]?.serviceName || `service-${idx}`;
        if (r.status === 'fulfilled') {
          const data = r.value?.data;
          if (data?.timeRange?.alignmentPeriodSeconds) alignSec = data.timeRange.alignmentPeriodSeconds;
          const reqMetric = data?.metrics?.['Request Count'];
          if (reqMetric?.points) {
            allPoints.push(...reqMetric.points);
            const svcTotal = reqMetric.points.reduce((s, p) => s + Math.round((p.value || 0) * alignSec), 0);
            perService[svcName] = (perService[svcName] || 0) + svcTotal;
          }
          const instMetric = data?.metrics?.['Instance Count'];
          if (instMetric?.points) {
            instancePoints.push(...instMetric.points);
          }
        }
      });
      allPoints.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      const NUM_BUCKETS = 48;
      const BUCKET_MS = 30 * 60 * 1000;
      const buckets = new Array(NUM_BUCKETS).fill(0);
      const bucketStart = now.getTime() - 24 * 60 * 60 * 1000;
      let totalRequests = 0;
      for (const p of allPoints) {
        const t = new Date(p.timestamp).getTime();
        const idx = Math.min(NUM_BUCKETS - 1, Math.floor((t - bucketStart) / BUCKET_MS));
        const actualCount = Math.round((p.value || 0) * alignSec);
        if (idx >= 0) buckets[idx] += actualCount;
        totalRequests += actualCount;
      }

      const instanceBuckets = new Array(NUM_BUCKETS).fill(0);
      const instanceBucketCounts = new Array(NUM_BUCKETS).fill(0);
      for (const p of instancePoints) {
        const t = new Date(p.timestamp).getTime();
        const idx = Math.min(NUM_BUCKETS - 1, Math.floor((t - bucketStart) / BUCKET_MS));
        if (idx >= 0) {
          instanceBuckets[idx] = Math.max(instanceBuckets[idx], Math.round(p.value || 0));
          instanceBucketCounts[idx]++;
        }
      }

      setRequestData({ buckets, totalRequests, perService, instanceBuckets });
    } catch { setRequestData(null); }
    setRequestDataLoading(false);
  }, [product]);

  useEffect(() => {
    fetchRequestData();
  }, [fetchRequestData]);

  const errorBuckets = useMemo(() => {
    if (!dashErrors.length) return new Array(48).fill(0);
    const now = new Date();
    const bucketStart = now.getTime() - 24 * 60 * 60 * 1000;
    const BUCKET_MS = 30 * 60 * 1000;
    const buckets = new Array(48).fill(0);
    for (const err of dashErrors) {
      const t = new Date(err.timestamp).getTime();
      const idx = Math.min(47, Math.floor((t - bucketStart) / BUCKET_MS));
      if (idx >= 0) buckets[idx]++;
    }
    return buckets;
  }, [dashErrors]);

  const errorRate = useMemo(() => {
    if (!requestData?.totalRequests) return null;
    return ((dashErrors.length / requestData.totalRequests) * 100);
  }, [dashErrors.length, requestData]);

  const [scalingData, setScalingData] = useState(null);
  const [scalingLoading, setScalingLoading] = useState(false);

  const [traces, setTraces] = useState([]);
  const [tracesLoading, setTracesLoading] = useState(false);
  const [traceTimeRange, setTraceTimeRange] = useState('1h');
  const [traceMethodFilter, setTraceMethodFilter] = useState('');
  const [traceStatusFilter, setTraceStatusFilter] = useState('');
  const [traceSpanStatusFilter, setTraceSpanStatusFilter] = useState('');
  const [traceServiceFilter, setTraceServiceFilter] = useState('');
  const [traceMinDuration, setTraceMinDuration] = useState('');

  const TRACE_TIME_OPTIONS = [
    { value: '15m', label: '15 min', ms: 15 * 60 * 1000 },
    { value: '30m', label: '30 min', ms: 30 * 60 * 1000 },
    { value: '1h', label: '1 hour', ms: 60 * 60 * 1000 },
    { value: '6h', label: '6 hours', ms: 6 * 60 * 60 * 1000 },
    { value: '24h', label: '24 hours', ms: 24 * 60 * 60 * 1000 },
  ];

  const fetchTraces = useCallback(async () => {
    if (!product?.cloudRuns?.length) return;
    setTracesLoading(true);
    try {
      const now = new Date();
      const rangeMs = TRACE_TIME_OPTIONS.find(o => o.value === traceTimeRange)?.ms || 60 * 60 * 1000;
      const startTime = new Date(now.getTime() - rangeMs).toISOString();
      const endTime = now.toISOString();
      const grouped = {};
      product.cloudRuns.forEach(cr => {
        if (!grouped[cr.gcpProjectId]) grouped[cr.gcpProjectId] = [];
        grouped[cr.gcpProjectId].push(cr.serviceName);
      });
      const results = await Promise.allSettled(
        Object.entries(grouped).map(([projId, names]) =>
          getCloudRunTraces(projId, names, { startTime, endTime, pageSize: 100 })
        )
      );
      const allTraces = [];
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value?.data?.traces) {
          allTraces.push(...r.value.data.traces);
        } else if (r.status === 'rejected') {
          console.error('Trace fetch failed:', r.reason?.message || r.reason);
        }
      });
      allTraces.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
      setTraces(allTraces);
    } catch (err) {
      console.error('Trace fetch error:', err);
      setTraces([]);
    }
    setTracesLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, traceTimeRange]);

  useEffect(() => {
    fetchTraces();
  }, [fetchTraces]);

  const fetchScaling = useCallback(async () => {
    if (!product?.cloudRuns?.length) return;
    setScalingLoading(true);
    try {
      const grouped = {};
      product.cloudRuns.forEach(cr => {
        const key = `${cr.gcpProjectId}__${cr.region || 'us-central1'}`;
        if (!grouped[key]) grouped[key] = { projectId: cr.gcpProjectId, region: cr.region || 'us-central1', names: [] };
        grouped[key].names.push(cr.serviceName);
      });
      const results = await Promise.allSettled(
        Object.values(grouped).map(g =>
          getServiceScaling(g.projectId, g.names, g.region).then(r => r.data)
        )
      );
      const merged = {};
      results.forEach(r => { if (r.status === 'fulfilled') Object.assign(merged, r.value); });
      setScalingData(merged);
    } catch (err) {
      console.error('Scaling fetch error:', err);
    } finally {
      setScalingLoading(false);
    }
  }, [product]);

  useEffect(() => {
    fetchScaling();
  }, [fetchScaling]);

  const filteredTraces = useMemo(() => {
    let result = traces;
    if (traceMethodFilter) {
      result = result.filter(t => t.httpMethod?.toUpperCase() === traceMethodFilter);
    }
    if (traceStatusFilter) {
      const s = parseInt(traceStatusFilter);
      if (traceStatusFilter === '2xx') result = result.filter(t => { const c = parseInt(t.httpStatus); return c >= 200 && c < 300; });
      else if (traceStatusFilter === '4xx') result = result.filter(t => { const c = parseInt(t.httpStatus); return c >= 400 && c < 500; });
      else if (traceStatusFilter === '5xx') result = result.filter(t => { const c = parseInt(t.httpStatus); return c >= 500; });
      else if (s) result = result.filter(t => parseInt(t.httpStatus) === s);
    }
    if (traceSpanStatusFilter) {
      result = result.filter(t => t.spanStatus === traceSpanStatusFilter);
    }
    if (traceServiceFilter) {
      result = result.filter(t => t.serviceName === traceServiceFilter);
    }
    if (traceMinDuration) {
      const match = traceMinDuration.match(/^(lt|gt)(\d+)$/);
      if (match) {
        const threshold = parseInt(match[2]);
        if (match[1] === 'lt') result = result.filter(t => t.durationMs < threshold);
        else result = result.filter(t => t.durationMs > threshold);
      }
    }
    return result;
  }, [traces, traceMethodFilter, traceStatusFilter, traceSpanStatusFilter, traceServiceFilter, traceMinDuration]);

  const traceServiceNames = useMemo(() => {
    const set = new Set();
    traces.forEach(t => { if (t.serviceName) set.add(t.serviceName); });
    return [...set].sort();
  }, [traces]);

  const serviceHealthMap = useMemo(() => {
    const map = {};
    if (!product?.cloudRuns) return map;
    product.cloudRuns.forEach((cr, index) => {
      const info = serviceInfoCache?.[`svc-${index}`];
      if (info?.conditions?.length > 0) {
        map[cr.serviceName] = info.conditions.every(c => c.state === 'CONDITION_SUCCEEDED') ? 'ready' : 'degraded';
      } else {
        map[cr.serviceName] = 'unknown';
      }
    });
    return map;
  }, [product, serviceInfoCache]);

  return (
    <div className="dashboard-tab">
      {/* Refresh Bar */}
      <div className="dash-refresh-bar">
        <div className="dash-refresh-left">
          <div className="dash-refresh-times">
            {dashErrorsLastRefreshed && (
              <span className="dash-refresh-time">Errors: {dashErrorsLastRefreshed.toLocaleTimeString()}{dashErrorsLoading ? ' ⟳' : ''}</span>
            )}
            {dashCostsLastRefreshed && (
              <span className="dash-refresh-time">Cost: {dashCostsLastRefreshed.toLocaleTimeString()}{dashCostsLoading ? ' ⟳' : ''}</span>
            )}
          </div>
          {dashCosts?.source === 'billing' ? (
            <span className="dash-source-badge billing">Exact (Billing)</span>
          ) : (
            <span className="dash-source-badge estimated">Estimated (Metrics)</span>
          )}
        </div>
        <div className="dash-refresh-right">
          <label className="dash-auto-toggle">
            <input type="checkbox" checked={dashAutoRefresh} onChange={(e) => setDashAutoRefresh(e.target.checked)} />
            Auto (Errors 2min · Cost 10min)
          </label>
          <button className="btn btn-sm btn-secondary" onClick={() => { fetchDashErrors(); fetchDashCosts(); }} disabled={dashCostsLoading && dashErrorsLoading}>
            <HiOutlineRefresh size={14} className={dashCostsLoading || dashErrorsLoading ? 'spin' : ''} /> Refresh All
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dash-summary-row">
        <div className="dash-summary-card clickable" onClick={() => onSwitchTab('services')}>
          <div className="dash-summary-icon" style={{ background: 'var(--alpha-blue-15)', color: 'var(--accent-blue)' }}>
            <HiOutlineServer size={18} />
          </div>
          <div className="dash-summary-info">
            <span className="dash-summary-value">{product?.cloudRuns?.length || 0}</span>
            <span className="dash-summary-label">Services</span>
          </div>
        </div>
        <div className="dash-summary-card clickable" onClick={() => onSwitchTab('logs', { severity: ['ERROR', 'CRITICAL'] })}>
          <div className="dash-summary-icon" style={{ background: 'var(--alpha-red-15)', color: 'var(--accent-red)' }}>
            <HiOutlineExclamationCircle size={18} />
          </div>
          <div className="dash-summary-info">
            <span className="dash-summary-value">{dashErrorsLoading ? '...' : dashErrors.length}</span>
            <span className="dash-summary-label">Errors (24h)</span>
            {!dashErrorsLoading && errorRate !== null && (
              <span className={`dash-error-rate ${dashErrors.length === 0 ? 'ok' : 'bad'}`}>
                {dashErrors.length === 0 ? '0%' : errorRate < 0.01 ? '<0.01%' : errorRate.toFixed(2) + '%'} of requests
              </span>
            )}
          </div>
          {!dashErrorsLoading && dashErrors.length > 0 && (
            <MiniSparkline data={errorBuckets} color="var(--accent-red)" />
          )}
        </div>
        <div className="dash-summary-card">
          <div className="dash-summary-icon" style={{ background: 'var(--alpha-green-15)', color: 'var(--accent-green)' }}>
            <HiOutlineCurrencyDollar size={18} />
          </div>
          <div className="dash-summary-info">
            <span className="dash-summary-value">
              {dashCostsLoading ? '...' : dashCosts ? `₹${Object.values(dashCosts.currentMonth?.services || {}).reduce((s, v) => s + (v.grossCostINR || v.totalCostINR || 0), 0).toFixed(2)}` : '₹0.00'}
            </span>
            <span className="dash-summary-label">Usage Cost (This Month)</span>
            {(() => {
              if (dashCostsLoading || !dashCosts) return null;
              const curr = Object.values(dashCosts.currentMonth?.services || {}).reduce((s, v) => s + (v.grossCostINR || v.totalCostINR || 0), 0);
              const prev = Object.values(dashCosts.previousMonth?.services || {}).reduce((s, v) => s + (v.grossCostINR || v.totalCostINR || 0), 0);
              if (prev === 0) return null;
              const pct = ((curr - prev) / prev * 100).toFixed(0);
              const isUp = curr > prev;
              return <span className={`dash-trend ${isUp ? 'up' : 'down'}`}>{isUp ? '↑' : '↓'} {Math.abs(pct)}%</span>;
            })()}
          </div>
        </div>
        <div className="dash-summary-card">
          <div className="dash-summary-icon" style={{ background: 'var(--alpha-purple-15)', color: 'var(--accent-purple, #a78bfa)' }}>
            <HiOutlineCurrencyDollar size={18} />
          </div>
          <div className="dash-summary-info">
            <span className="dash-summary-value">
              {dashCostsLoading ? '...' : dashCosts ? `₹${Object.values(dashCosts.previousMonth?.services || {}).reduce((s, v) => s + (v.grossCostINR || v.totalCostINR || 0), 0).toFixed(2)}` : '₹0.00'}
            </span>
            <span className="dash-summary-label">Usage Cost (Last Month)</span>
          </div>
        </div>
      </div>

      {/* Service Health Chips */}
      {product?.cloudRuns?.length > 0 && (
        <div className="dash-health-chips">
          {product.cloudRuns.map((cr, i) => {
            const status = serviceHealthMap[cr.serviceName] || 'unknown';
            return (
              <div key={i} className={`dash-health-chip ${status}`} onClick={() => onSwitchTab('services')}>
                <span className="dash-health-dot"></span>
                <span>{cr.serviceName}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Request Count Chart */}
      {requestData && (
        <RequestChart
          buckets={requestData.buckets}
          totalRequests={requestData.totalRequests}
          perService={requestData.perService}
          loading={requestDataLoading}
        />
      )}

      {/* Service Resource Utilization (30 days) */}
      <div className="dash-section">
        <div className="dash-scaling-header">
          <h3 className="dash-section-title" style={{ margin: 0 }}>
            <HiOutlineServer size={16} /> Resource Utilization (30 days)
          </h3>
          <button className="dash-refresh-btn" onClick={fetchScaling} disabled={scalingLoading} title="Refresh">
            <HiOutlineRefresh size={14} className={scalingLoading ? 'spin' : ''} />
          </button>
        </div>
        {scalingLoading && !scalingData ? (
          <div className="skeleton-card" style={{ height: 120 }} />
        ) : scalingData && Object.keys(scalingData).length > 0 ? (
          <div className="dash-util-grid">
            {Object.entries(scalingData).map(([svc, data]) => {
              const s = data.summary || {};
              const cfg = s.config || {};
              const cpuColor = v => v > 70 ? 'high' : v > 40 ? 'mid' : 'low';
              const memColor = v => v > 70 ? 'high' : v > 40 ? 'mid' : 'low';
              return (
                <div key={svc} className="dash-util-card">
                  <div className="dash-util-card-header">
                    <span className="dash-util-svc-name">{svc}</span>
                    <ConfigTags cfg={cfg} />
                  </div>
                  <div className="dash-util-metrics-grid">
                    <div className="dash-util-metric-block">
                      <div className="dash-util-metric-title">CPU Utilization</div>
                      <div className="dash-util-bar-bg"><div className="dash-util-bar cpu" style={{ width: `${Math.min(s.cpuMax || 0, 100)}%` }}><div className="dash-util-bar-avg" style={{ width: s.cpuMax > 0 ? `${(s.cpuAvg / s.cpuMax) * 100}%` : '0%' }} /></div></div>
                      <div className="dash-util-metric-nums">
                        <span className={cpuColor(s.cpuMin)}>{s.cpuMin}%<small>min</small></span>
                        <span className={cpuColor(s.cpuAvg)}>{s.cpuAvg}%<small>avg</small></span>
                        <span className={cpuColor(s.cpuMax)}>{s.cpuMax}%<small>max</small></span>
                      </div>
                    </div>
                    <div className="dash-util-metric-block">
                      <div className="dash-util-metric-title">Memory Utilization</div>
                      <div className="dash-util-bar-bg"><div className="dash-util-bar mem" style={{ width: `${Math.min(s.memMax || 0, 100)}%` }}><div className="dash-util-bar-avg" style={{ width: s.memMax > 0 ? `${(s.memAvg / s.memMax) * 100}%` : '0%' }} /></div></div>
                      <div className="dash-util-metric-nums">
                        <span className={memColor(s.memMin)}>{s.memMin}%<small>min</small></span>
                        <span className={memColor(s.memAvg)}>{s.memAvg}%<small>avg</small></span>
                        <span className={memColor(s.memMax)}>{s.memMax}%<small>max</small></span>
                      </div>
                    </div>
                    <div className="dash-util-metric-block">
                      <div className="dash-util-metric-title">Instances</div>
                      <div className="dash-util-inst-row">
                        <div className="dash-util-inst-item"><span className="dash-util-inst-val accent">{s.currentInstances}</span><small>now</small></div>
                        <div className="dash-util-inst-item"><span className="dash-util-inst-val warn">{s.peakInstances}</span><small>peak</small></div>
                        <div className="dash-util-inst-item"><span className="dash-util-inst-val">{s.minInstances}</span><small>min</small></div>
                      </div>
                    </div>
                  </div>
                  {s.suggestions?.length > 0 && (
                    <div className="dash-util-suggestions">
                      {s.suggestions.map((sg, i) => (
                        <SuggestionRow key={i} sg={sg} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : !scalingLoading ? (
          <div className="dash-no-errors">
            <span className="dash-no-errors-icon">—</span>
            <p>No utilization data available.</p>
          </div>
        ) : null}
      </div>

      {/* Recent Traces */}
      <div className="dash-section dash-traces-card">
        <div className="dash-traces-header">
          <h3 className="dash-section-title" style={{ margin: 0 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ verticalAlign: '-2px' }}>
              <path d="M2 4h12M2 8h8M2 12h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="13" cy="8" r="1.5" fill="var(--accent-cyan)"/>
              <circle cx="10" cy="12" r="1.5" fill="var(--accent-green)"/>
            </svg>
            {' '}Recent Traces
            {!tracesLoading && (
              <span className="dash-error-count-badge">
                {filteredTraces.length}{filteredTraces.length !== traces.length ? ` / ${traces.length}` : ''} traces
              </span>
            )}
          </h3>
          <button className="btn btn-sm btn-secondary" onClick={fetchTraces} disabled={tracesLoading}>
            <HiOutlineRefresh size={13} className={tracesLoading ? 'spin' : ''} />
          </button>
        </div>

        <div className="dash-traces-filters">
          <select className="trace-filter-select" value={traceTimeRange} onChange={e => setTraceTimeRange(e.target.value)}>
            {TRACE_TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="trace-filter-select" value={traceMethodFilter} onChange={e => setTraceMethodFilter(e.target.value)}>
            <option value="">All Methods</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
            <option value="PATCH">PATCH</option>
            <option value="HEAD">HEAD</option>
            <option value="OPTIONS">OPTIONS</option>
          </select>
          <select className="trace-filter-select" value={traceStatusFilter} onChange={e => setTraceStatusFilter(e.target.value)}>
            <option value="">All HTTP Status</option>
            <option value="2xx">2xx Success</option>
            <option value="4xx">4xx Client Error</option>
            <option value="5xx">5xx Server Error</option>
          </select>
          <select className="trace-filter-select" value={traceSpanStatusFilter} onChange={e => setTraceSpanStatusFilter(e.target.value)}>
            <option value="">Span Status</option>
            <option value="Unset">Unset</option>
            <option value="Success">Success</option>
            <option value="Error">Error</option>
          </select>
          {traceServiceNames.length > 1 && (
            <select className="trace-filter-select" value={traceServiceFilter} onChange={e => setTraceServiceFilter(e.target.value)}>
              <option value="">All Services</option>
              {traceServiceNames.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <select className="trace-filter-select" value={traceMinDuration} onChange={e => setTraceMinDuration(e.target.value)}>
            <option value="">Duration</option>
            <option value="lt60000">&lt; 1 min</option>
            <option value="lt120000">&lt; 2 min</option>
            <option value="lt180000">&lt; 3 min</option>
            <option value="lt300000">&lt; 5 min</option>
            <option value="lt600000">&lt; 10 min</option>
            <option value="gt600000">&gt; 10 min</option>
          </select>
          {(traceMethodFilter || traceStatusFilter || traceSpanStatusFilter || traceServiceFilter || traceMinDuration) && (
            <button className="trace-filter-clear" onClick={() => { setTraceMethodFilter(''); setTraceStatusFilter(''); setTraceSpanStatusFilter(''); setTraceServiceFilter(''); setTraceMinDuration(''); }}>
              Clear
            </button>
          )}
        </div>

        {tracesLoading ? (
          <div className="skeleton-card" style={{ padding: 0, overflow: 'hidden' }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 10 }}>
                <div className="skeleton" style={{ width: 50, height: 13 }}></div>
                <div className="skeleton" style={{ width: 80, height: 13 }}></div>
                <div className="skeleton" style={{ flex: 1, height: 13 }}></div>
                <div className="skeleton" style={{ width: 50, height: 13 }}></div>
              </div>
            ))}
          </div>
        ) : traces.length === 0 ? (
          <div className="dash-no-errors">
            <span className="dash-no-errors-icon" style={{ color: 'var(--text-muted)' }}>—</span>
            <p>No traces found. Ensure the OpenTelemetry SDK is active on your Cloud Run service.</p>
          </div>
        ) : filteredTraces.length === 0 ? (
          <div className="dash-no-errors">
            <span className="dash-no-errors-icon" style={{ color: 'var(--text-muted)' }}>—</span>
            <p>No traces match the current filters.</p>
          </div>
        ) : (
          <div className="dash-traces-list">
            <div className="dash-traces-table-header">
              <div className="dash-trace-col-chevron"></div>
              <div className="dash-trace-col-spanid">Span ID</div>
              <div className="dash-trace-col-method">Method</div>
              <div className="dash-trace-col-url">Endpoint</div>
              <div className="dash-trace-col-svc">Service</div>
              <div className="dash-trace-col-status">Status</div>
              <div className="dash-trace-col-dur">Duration</div>
              <div className="dash-trace-col-spans">Spans</div>
              <div className="dash-trace-col-time">Time</div>
            </div>
            {filteredTraces.map((t, i) => (
              <TraceRow key={t.traceId || i} trace={t} />
            ))}
          </div>
        )}
      </div>

      {/* Instance Count Mini-Chart */}
      {requestData?.instanceBuckets && (
        <InstanceMiniChart buckets={requestData.instanceBuckets} />
      )}

      {/* Latest Errors (Grouped) */}
      <div className="dash-section">
        <h3 className="dash-section-title">
          <HiOutlineExclamationCircle size={18} /> Latest Errors (Last 24 Hours)
          {!dashErrorsLoading && dashErrors.length > 0 && (
            <span className="dash-error-count-badge">{dashErrors.length} total · {groupedErrors.length} unique</span>
          )}
        </h3>
        {dashErrorsLoading ? (
          <div className="skeleton-card" style={{ padding: 0, overflow: 'hidden' }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 12 }}>
                <div className="skeleton" style={{ width: 80, height: 14 }}></div>
                <div className="skeleton" style={{ width: 120, height: 14 }}></div>
                <div className="skeleton" style={{ flex: 1, height: 14 }}></div>
              </div>
            ))}
          </div>
        ) : dashErrors.length === 0 ? (
          <div className="dash-no-errors">
            <span className="dash-no-errors-icon">✓</span>
            <p>No errors in the last 24 hours</p>
          </div>
        ) : (
          <div className="dash-errors-list">
            {groupedErrors.map((group, i) => (
              <DashErrorRow key={i} err={group.err} message={group.message} count={group.count} latest={group.latest} />
            ))}
          </div>
        )}
      </div>

      {/* Cost Breakdown */}
      <div className="dash-section">
        <div className="dash-cost-section-header">
          <h3 className="dash-section-title" style={{ margin: 0 }}><HiOutlineCurrencyDollar size={18} /> Cost Breakdown by Service (₹ INR)</h3>
          <select
            className="trace-filter-select"
            value={costMonth}
            onChange={e => setCostMonth(parseInt(e.target.value))}
            disabled={dashCostsLoading}
          >
            {COST_MONTH_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {dashCostsLoading ? (
          <div className="skeleton-cost-loader">
            <div className="skeleton-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', background: 'var(--bg-primary)' }}>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: 12 }}></div>
              </div>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color)' }}>
                  <div className="skeleton skeleton-text" style={{ width: `${70 - i * 10}%` }}></div>
                </div>
              ))}
            </div>
          </div>
        ) : !dashCosts ? (
          <div className="empty-state"><p>No cost data available</p></div>
        ) : (
          <>
            <div className="dash-cost-table">
              <div className="dash-cost-header">
                <div className="dash-cost-col-name">Service</div>
                <div className="dash-cost-col">CPU / Mem</div>
                <div className="dash-cost-col">Billable Hrs</div>
                <div className="dash-cost-col">Requests</div>
                <div className="dash-cost-col">CPU (₹)</div>
                <div className="dash-cost-col">Memory (₹)</div>
                <div className="dash-cost-col">{getMonthLabel(costMonth)} (₹)</div>
                <div className="dash-cost-col">{getMonthLabel(costMonth + 1)} (₹)</div>
              </div>
              {Object.keys(dashCosts.currentMonth?.services || {}).map(svc => {
                const cur = dashCosts.currentMonth.services[svc] || {};
                const prev = dashCosts.previousMonth?.services?.[svc] || {};
                const curCost = cur.grossCostINR || cur.totalCostINR || 0;
                const prevCost = prev.grossCostINR || prev.totalCostINR || 0;
                return (
                  <div key={svc} className="dash-cost-row">
                    <div className="dash-cost-col-name">{svc}</div>
                    <div className="dash-cost-col">{cur.cpuVcpus || '-'} vCPU / {cur.memGiB || '-'} GiB</div>
                    <div className="dash-cost-col">{cur.billableInstanceHours || 0}h</div>
                    <div className="dash-cost-col">{(cur.requestCount || 0).toLocaleString()}</div>
                    <div className="dash-cost-col" style={{ color: 'var(--accent-cyan)' }}>₹{(cur.costBreakdown?.cpuINR || 0).toFixed(2)}</div>
                    <div className="dash-cost-col" style={{ color: 'var(--accent-purple)' }}>₹{(cur.costBreakdown?.memoryINR || 0).toFixed(2)}</div>
                    <div className="dash-cost-col dash-cost-inr">₹{curCost.toFixed(2)}</div>
                    <div className="dash-cost-col" style={{ color: 'var(--text-muted)' }}>₹{prevCost.toFixed(2)}</div>
                  </div>
                );
              })}
              <div className="dash-cost-row dash-cost-total">
                <div className="dash-cost-col-name">Total</div>
                <div className="dash-cost-col"></div>
                <div className="dash-cost-col">
                  {Object.values(dashCosts.currentMonth?.services || {}).reduce((s, v) => s + (v.billableInstanceHours || 0), 0).toFixed(1)}h
                </div>
                <div className="dash-cost-col">
                  {Object.values(dashCosts.currentMonth?.services || {}).reduce((s, v) => s + (v.requestCount || 0), 0).toLocaleString()}
                </div>
                <div className="dash-cost-col" style={{ color: 'var(--accent-cyan)' }}>
                  ₹{Object.values(dashCosts.currentMonth?.services || {}).reduce((s, v) => s + (v.costBreakdown?.cpuINR || 0), 0).toFixed(2)}
                </div>
                <div className="dash-cost-col" style={{ color: 'var(--accent-purple)' }}>
                  ₹{Object.values(dashCosts.currentMonth?.services || {}).reduce((s, v) => s + (v.costBreakdown?.memoryINR || 0), 0).toFixed(2)}
                </div>
                <div className="dash-cost-col dash-cost-inr">
                  ₹{Object.values(dashCosts.currentMonth?.services || {}).reduce((s, v) => s + (v.grossCostINR || v.totalCostINR || 0), 0).toFixed(2)}
                </div>
                <div className="dash-cost-col" style={{ color: 'var(--text-muted)' }}>
                  ₹{Object.values(dashCosts.previousMonth?.services || {}).reduce((s, v) => s + (v.grossCostINR || v.totalCostINR || 0), 0).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="dash-cost-periods">
              <span>Current: {dashCosts.currentMonth?.period}</span>
              <span>Previous: {dashCosts.previousMonth?.period}</span>
              <span>Rate: 1 USD = ₹{dashCosts.exchangeRate}</span>
            </div>
            <div className="dash-cost-note">
              {dashCosts?.source === 'billing'
                ? 'Usage cost from GCP Billing Export (BigQuery) — matches GCP Console.'
                : 'Usage cost estimated from GCP allocation metrics × actual GCP INR pricing rates. For exact costs, configure GCP_BILLING_TABLE in .env.'
              }
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MiniSparkline({ data, color }) {
  const max = Math.max(...data, 1);
  const W = 60, H = 24;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - (v / max) * (H - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p}`).join(' ');
  const area = line + ` L${W},${H} L0,${H} Z`;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="dash-mini-sparkline">
      <defs>
        <linearGradient id="miniSparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#miniSparkGrad)" />
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InstanceMiniChart({ buckets }) {
  const max = Math.max(...buckets, 1);
  const peak = Math.max(...buckets);
  const current = buckets[buckets.length - 1];
  const avg = (buckets.reduce((a, b) => a + b, 0) / buckets.length).toFixed(1);
  const hasData = buckets.some(v => v > 0);

  if (!hasData) return null;

  const W = 500, H = 50, PAD_L = 0, PAD_R = 0;
  const chartW = W - PAD_L - PAD_R;
  const pts = buckets.map((v, i) => {
    const x = PAD_L + (i / (buckets.length - 1)) * chartW;
    const y = H - (v / max) * (H - 4) - 2;
    return { x, y };
  });
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = line + ` L${pts[pts.length - 1].x.toFixed(1)},${H} L${pts[0].x.toFixed(1)},${H} Z`;

  return (
    <div className="dash-section dash-instance-card">
      <div className="dash-instance-header">
        <h3 className="dash-section-title" style={{ margin: 0 }}>
          <HiOutlineServer size={16} /> Instances (Last 24h)
        </h3>
        <div className="dash-instance-stats">
          <div className="dash-instance-stat">
            <span className="dash-instance-stat-val accent">{current}</span>
            <span className="dash-instance-stat-lbl">Current</span>
          </div>
          <div className="dash-instance-stat">
            <span className="dash-instance-stat-val">{peak}</span>
            <span className="dash-instance-stat-lbl">Peak</span>
          </div>
          <div className="dash-instance-stat">
            <span className="dash-instance-stat-val">{avg}</span>
            <span className="dash-instance-stat-lbl">Avg</span>
          </div>
        </div>
      </div>
      <div className="dash-instance-chart-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="dash-instance-svg">
          <defs>
            <linearGradient id="instGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(16,185,129,0.35)" />
              <stop offset="100%" stopColor="rgba(16,185,129,0.02)" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#instGrad)" />
          <path d={line} fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r="3" fill="#10b981" stroke="var(--bg-card)" strokeWidth="1.5" />
        </svg>
      </div>
    </div>
  );
}

function ConfigTags({ cfg }) {
  return (
    <div className="dash-util-config-tags">
      {cfg.cpu && <span className="dash-util-tag">CPU {cfg.cpu}</span>}
      {cfg.memory && <span className="dash-util-tag">Mem {cfg.memory}</span>}
      <span className="dash-util-tag">Inst {cfg.minInstances}–{cfg.maxInstances}</span>
      {cfg.concurrency > 0 && <span className="dash-util-tag">Concurrency {cfg.concurrency}</span>}
      {cfg.cpuAlwaysAllocated && <span className="dash-util-tag">CPU Always Allocated</span>}
    </div>
  );
}

function SuggestionRow({ sg }) {
  return (
    <div className={`dash-util-sug ${sg.type}`}>
      <span className="dash-util-sug-icon">
        {sg.type === 'scale-up' ? '▲' : sg.type === 'reduce' ? '▼' : sg.type === 'pause' ? '⏸' : '●'}
      </span>
      <span className="dash-util-sug-text">{sg.text}</span>
    </div>
  );
}

function TraceRow({ trace }) {
  const [expanded, setExpanded] = useState(false);
  const t = trace;
  const statusNum = parseInt(t.httpStatus) || 0;
  const statusClass = statusNum >= 500 ? 'error' : statusNum >= 400 ? 'warn' : 'ok';
  const durLabel = t.durationMs >= 1000
    ? (t.durationMs / 1000).toFixed(2) + 's'
    : t.durationMs + 'ms';

  const shortUrl = (t.httpUrl || t.spans?.[0]?.name || '—').replace(/^https?:\/\/[^/]+/, '');

  return (
    <div className={`dash-trace-row ${expanded ? 'expanded' : ''}`}>
      <div className="dash-trace-row-main" onClick={() => setExpanded(!expanded)}>
        <div className="dash-trace-col-chevron">
          <span className={`trace-chevron ${expanded ? 'open' : ''}`}>&#9654;</span>
        </div>
        <div className="dash-trace-col-spanid" title={t.rootSpanId || t.spans?.[0]?.spanId || ''}>
          <span className="trace-detail-mono">{t.rootSpanId || t.spans?.[0]?.spanId || '—'}</span>
        </div>
        <div className="dash-trace-col-method">
          {t.httpMethod && <span className={`trace-method ${t.httpMethod.toLowerCase()}`}>{t.httpMethod}</span>}
        </div>
        <div className="dash-trace-col-url" title={t.httpUrl}>{shortUrl}</div>
        <div className="dash-trace-col-svc">{t.serviceName || '—'}</div>
        <div className="dash-trace-col-status">
          {statusNum > 0 && <span className={`trace-status ${statusClass}`}>{statusNum}</span>}
          <span className={`trace-span-status ${(t.spanStatus || 'Unset').toLowerCase()}`}>{t.spanStatus || 'Unset'}</span>
        </div>
        <div className="dash-trace-col-dur">
          <span className={`trace-dur ${t.durationMs > 1000 ? 'slow' : ''}`}>{durLabel}</span>
        </div>
        <div className="dash-trace-col-spans">{t.spanCount}</div>
        <div className="dash-trace-col-time">{t.startTime ? new Date(t.startTime).toLocaleTimeString() : '—'}</div>
      </div>
      {expanded && (
        <div className="dash-trace-expanded" onClick={e => e.stopPropagation()}>
          {/* Trace & Span IDs */}
          <div className="trace-detail-section">
            <div className="trace-detail-title">Trace Info</div>
            <div className="trace-detail-grid">
              <div className="trace-detail-item">
                <span className="trace-detail-label">Trace ID</span>
                <span className="trace-detail-mono">{t.traceId}</span>
              </div>
              {(t.rootSpanId || t.spans?.[0]?.spanId) && (
                <div className="trace-detail-item">
                  <span className="trace-detail-label">Root Span ID</span>
                  <span className="trace-detail-mono">{t.rootSpanId || t.spans[0].spanId}</span>
                </div>
              )}
              <div className="trace-detail-item">
                <span className="trace-detail-label">Start Time</span>
                <span className="trace-detail-mono">{t.startTime ? new Date(t.startTime).toLocaleString() : '—'}</span>
              </div>
              <div className="trace-detail-item">
                <span className="trace-detail-label">Duration</span>
                <span className="trace-detail-mono">{durLabel}</span>
              </div>
              {t.serviceName && (
                <div className="trace-detail-item">
                  <span className="trace-detail-label">Service</span>
                  <span className="trace-detail-mono">{t.serviceName}</span>
                </div>
              )}
              {t.httpMethod && (
                <div className="trace-detail-item">
                  <span className="trace-detail-label">HTTP</span>
                  <span className="trace-detail-mono">{t.httpMethod} {t.httpUrl} → {t.httpStatus || '—'}</span>
                </div>
              )}
            </div>
          </div>

          {/* Waterfall */}
          {t.spans?.length > 0 && (
            <div className="trace-detail-section">
              <div className="trace-detail-title">Spans ({t.spanCount})</div>
              <TraceWaterfall spans={t.spans} totalMs={t.durationMs} />
            </div>
          )}

          {/* Individual Span Details */}
          {t.spans?.length > 0 && (
            <div className="trace-detail-section">
              <div className="trace-detail-title">Span Details</div>
              <div className="trace-span-cards">
                {t.spans.map((span, i) => {
                  const sDur = Math.max(new Date(span.endTime).getTime() - new Date(span.startTime).getTime(), 0);
                  const sDurLabel = sDur >= 1000 ? (sDur / 1000).toFixed(2) + 's' : sDur + 'ms';
                  const labelEntries = Object.entries(span.labels || {});
                  return (
                    <div key={span.spanId || i} className="trace-span-card">
                      <div className="trace-span-card-header">
                        <span className="trace-span-name">{span.name || `span-${i}`}</span>
                        <span className="trace-span-dur">{sDurLabel}</span>
                      </div>
                      <div className="trace-span-meta">
                        <span className="trace-detail-label">Span ID</span>
                        <span className="trace-detail-mono">{span.spanId}</span>
                      </div>
                      {span.parentSpanId && (
                        <div className="trace-span-meta">
                          <span className="trace-detail-label">Parent</span>
                          <span className="trace-detail-mono">{span.parentSpanId}</span>
                        </div>
                      )}
                      <div className="trace-span-meta">
                        <span className="trace-detail-label">Kind</span>
                        <span className="trace-detail-mono">{span.kind}</span>
                      </div>
                      <div className="trace-span-meta">
                        <span className="trace-detail-label">Start</span>
                        <span className="trace-detail-mono">{new Date(span.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })}</span>
                      </div>
                      {labelEntries.length > 0 && (
                        <div className="trace-span-labels">
                          <span className="trace-detail-label">Labels</span>
                          <div className="trace-span-label-list">
                            {labelEntries.map(([k, v]) => (
                              <div key={k} className="trace-span-label-row">
                                <span className="trace-span-label-key">{k}</span>
                                <span className="trace-span-label-val">{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TraceWaterfall({ spans, totalMs }) {
  if (!spans?.length || !totalMs) return null;
  const rootStart = new Date(spans[0].startTime).getTime();
  const total = Math.max(totalMs, 1);

  return (
    <div className="trace-waterfall-wrap">
      {spans.map((span, i) => {
        const start = new Date(span.startTime).getTime() - rootStart;
        const dur = Math.max(new Date(span.endTime).getTime() - new Date(span.startTime).getTime(), 1);
        const left = (start / total) * 100;
        const width = Math.max((dur / total) * 100, 0.5);
        const depth = span.parentSpanId ? 1 : 0;
        const spanLabel = span.name || span.labels?.['http/url'] || `span-${i}`;
        const durStr = dur >= 1000 ? (dur / 1000).toFixed(1) + 's' : dur + 'ms';

        return (
          <div key={span.spanId || i} className="trace-wf-row" style={{ paddingLeft: depth * 18 }}>
            <div className="trace-wf-name" title={spanLabel}>
              {spanLabel}
            </div>
            <div className="trace-wf-bar-wrap">
              <div className="trace-wf-bar"
                style={{ left: `${left}%`, width: `${width}%`, background: depth === 0 ? 'var(--accent-cyan)' : '#6bcb77' }}
              />
            </div>
            <div className="trace-wf-dur">{durStr}</div>
          </div>
        );
      })}
    </div>
  );
}

const PIE_COLORS = [
  '#2563eb', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6',
  '#f97316', '#06b6d4', '#ec4899', '#14b8a6', '#d946ef',
];

function RequestChart({ buckets, totalRequests, perService, loading }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const chartAccent = isLight ? '#2563eb' : '#00e5ff';
  const chartAccentDark = isLight ? '#1d4ed8' : '#0097a7';
  const chartGradTop = isLight ? 'rgba(37,99,235,0.25)' : 'rgba(0,229,255,0.35)';
  const chartGradBottom = isLight ? 'rgba(37,99,235,0.02)' : 'rgba(0,229,255,0.02)';

  const [chartType, setChartType] = useState('bar');
  const [hoverIndex, setHoverIndex] = useState(null);
  const [pieHover, setPieHover] = useState(null);
  const svgRef = useRef(null);

  const max = Math.max(...buckets, 1);
  const avg = buckets.reduce((a, b) => a + b, 0) / buckets.length;
  const peak = Math.max(...buckets);
  const peakIdx = buckets.indexOf(peak);

  const W = 700, H = 140, PAD_L = 40, PAD_R = 10, PAD_T = 10, PAD_B = 28;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const BAR_GAP = 1;
  const barW = (chartW - BAR_GAP * (buckets.length - 1)) / buckets.length;
  const slotW = barW + BAR_GAP;

  const now = useMemo(() => new Date(), []);

  const serviceEntries = useMemo(() => {
    if (!perService) return [];
    return Object.entries(perService)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [perService]);

  const pieSlices = useMemo(() => {
    if (!serviceEntries.length || totalRequests === 0) return [];
    let startAngle = -Math.PI / 2;
    const R = 60;
    return serviceEntries.map((entry, i) => {
      const fraction = entry.count / totalRequests;
      const angle = fraction * 2 * Math.PI;
      const endAngle = startAngle + angle;
      const midAngle = startAngle + angle / 2;
      let d;
      if (fraction <= 0) {
        d = '';
      } else if (fraction > 0.999) {
        d = `M 0 -${R} A ${R} ${R} 0 1 1 0 ${R} A ${R} ${R} 0 1 1 0 -${R} Z`;
      } else {
        const largeArc = angle > Math.PI ? 1 : 0;
        const x1 = Math.cos(startAngle) * R;
        const y1 = Math.sin(startAngle) * R;
        const x2 = Math.cos(endAngle) * R;
        const y2 = Math.sin(endAngle) * R;
        d = `M 0 0 L ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      }
      const slice = {
        name: entry.name,
        count: entry.count,
        pct: (fraction * 100).toFixed(1),
        color: PIE_COLORS[i % PIE_COLORS.length],
        d,
        midAngle,
      };
      startAngle = endAngle;
      return slice;
    }).filter(s => s.d);
  }, [serviceEntries, totalRequests]);

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(pct => ({
    y: PAD_T + chartH * (1 - pct),
    val: Math.round(max * pct),
  }));

  const fmtVal = (v) => {
    if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
    if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
    return String(v);
  };

  const pathPoints = buckets.map((val, i) => {
    const x = PAD_L + i * slotW + barW / 2;
    const y = PAD_T + chartH - (val / max) * chartH;
    return { x, y };
  });

  const linePath = pathPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L${pathPoints[pathPoints.length - 1].x.toFixed(1)},${PAD_T + chartH} L${pathPoints[0].x.toFixed(1)},${PAD_T + chartH} Z`;

  const getTimeLabel = (idx) =>
    new Date(now.getTime() - (47 - idx) * 30 * 60 * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const peakTime = getTimeLabel(peakIdx);

  const handleMouseMove = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
    const idx = Math.floor((svgPt.x - PAD_L) / slotW);
    if (idx >= 0 && idx < buckets.length) {
      setHoverIndex(idx);
    } else {
      setHoverIndex(null);
    }
  }, [buckets.length, slotW]);

  const handleMouseLeave = useCallback(() => {
    setHoverIndex(null);
  }, []);

  return (
    <div className="dash-charts-row">
      {/* Bar / Line Chart Card */}
      <div className="dash-section dash-request-card">
        <div className="dash-req-header">
          <div className="dash-req-header-left">
            <h3 className="dash-section-title" style={{ margin: 0 }}>Requests (Last 24h)</h3>
          </div>
          <div className="dash-req-header-right">
            <div className="dash-req-stats">
              <div className="dash-req-stat">
                <span className="dash-req-stat-val accent">{loading ? '...' : fmtVal(totalRequests)}</span>
                <span className="dash-req-stat-lbl">Total</span>
              </div>
              <div className="dash-req-stat">
                <span className="dash-req-stat-val">{fmtVal(peak)}</span>
                <span className="dash-req-stat-lbl">Peak ({peakTime})</span>
              </div>
              <div className="dash-req-stat">
                <span className="dash-req-stat-val">{fmtVal(Math.round(avg))}</span>
                <span className="dash-req-stat-lbl">Avg / 30min</span>
              </div>
            </div>
            <div className="dash-req-toggle">
              <button className={`mc-toggle-btn ${chartType === 'bar' ? 'active' : ''}`} onClick={() => setChartType('bar')} title="Bar Chart">
                <svg width="14" height="14" viewBox="0 0 14 14"><rect x="1" y="6" width="3" height="7" rx="0.5" fill="currentColor"/><rect x="5.5" y="3" width="3" height="10" rx="0.5" fill="currentColor"/><rect x="10" y="1" width="3" height="12" rx="0.5" fill="currentColor"/></svg>
              </button>
              <button className={`mc-toggle-btn ${chartType === 'line' ? 'active' : ''}`} onClick={() => setChartType('line')} title="Line Chart">
                <svg width="14" height="14" viewBox="0 0 14 14"><polyline points="1,11 4,6 7,8 10,3 13,5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
          </div>
        </div>

        <div className="dash-req-tooltip-row">
          {hoverIndex !== null ? (
            <>
              <span className="dash-req-tooltip-val">{buckets[hoverIndex].toLocaleString()}</span>
              <span className="dash-req-tooltip-lbl">requests at {getTimeLabel(hoverIndex)}</span>
            </>
          ) : (
            <span className="dash-req-tooltip-lbl">Hover over the chart to see details</span>
          )}
        </div>

        <div className="dash-req-chart-wrap" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="dash-req-svg">
            <defs>
              <linearGradient id="reqGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartGradTop} />
                <stop offset="100%" stopColor={chartGradBottom} />
              </linearGradient>
              <linearGradient id="reqBarGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartAccent} />
                <stop offset="100%" stopColor={chartAccentDark} />
              </linearGradient>
            </defs>

            {yTicks.map((tick, i) => (
              <g key={i}>
                <line x1={PAD_L} x2={W - PAD_R} y1={tick.y} y2={tick.y}
                  stroke="var(--border-color)" strokeWidth="0.5" strokeDasharray={i === 0 ? 'none' : '4,3'} opacity="0.5" />
                <text x={PAD_L - 6} y={tick.y + 3} textAnchor="end" fill="var(--text-muted)" fontSize="8">{fmtVal(tick.val)}</text>
              </g>
            ))}

            {avg > 0 && (
              <line x1={PAD_L} x2={W - PAD_R}
                y1={PAD_T + chartH - (avg / max) * chartH}
                y2={PAD_T + chartH - (avg / max) * chartH}
                stroke="#f59e0b" strokeWidth="0.7" strokeDasharray="6,3" opacity="0.6" />
            )}

            {chartType === 'bar' ? (
              buckets.map((val, i) => {
                const barH = (val / max) * chartH;
                const x = PAD_L + i * slotW;
                const y = PAD_T + chartH - barH;
                const isHover = hoverIndex === i;
                return (
                  <rect key={i} x={x} y={y} width={barW} height={Math.max(barH, 0.5)} rx={1.5}
                    fill={isHover ? (isLight ? '#1d4ed8' : '#33ecff') : 'url(#reqBarGrad)'}
                    opacity={isHover ? 1 : 0.8}
                    style={{ pointerEvents: 'none' }} />
                );
              })
            ) : (
              <>
                <path d={areaPath} fill="url(#reqGrad)" style={{ pointerEvents: 'none' }} />
                <path d={linePath} fill="none" stroke={chartAccent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ pointerEvents: 'none' }} />
                {hoverIndex !== null && pathPoints[hoverIndex] && (
                  <>
                    <line x1={pathPoints[hoverIndex].x} x2={pathPoints[hoverIndex].x}
                      y1={PAD_T} y2={PAD_T + chartH} stroke="var(--text-muted)" strokeWidth="0.5" strokeDasharray="3,2" opacity="0.5"
                      style={{ pointerEvents: 'none' }} />
                    <circle cx={pathPoints[hoverIndex].x} cy={pathPoints[hoverIndex].y} r="4"
                      fill={chartAccent} stroke="var(--bg-card)" strokeWidth="2" style={{ pointerEvents: 'none' }} />
                  </>
                )}
              </>
            )}

            {hoverIndex !== null && (
              <line x1={PAD_L + hoverIndex * slotW + barW / 2} x2={PAD_L + hoverIndex * slotW + barW / 2}
                y1={PAD_T} y2={PAD_T + chartH}
                stroke="var(--text-muted)" strokeWidth="0.5" strokeDasharray="3,2" opacity="0.4"
                style={{ pointerEvents: 'none' }} />
            )}

            {buckets.map((_, i) => {
              if (i % 4 !== 0) return null;
              const cx = PAD_L + i * slotW + barW / 2;
              const lbl = getTimeLabel(i);
              return (
                <g key={`lbl-${i}`}>
                  <line x1={cx} x2={cx} y1={PAD_T + chartH} y2={PAD_T + chartH + 4} stroke="var(--text-muted)" strokeWidth="0.5" opacity="0.4" />
                  <text x={cx} y={H - 4} textAnchor="middle" fill="var(--text-muted)" fontSize="7.5">{lbl}</text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Pie Chart Card */}
      {serviceEntries.length > 0 && (
        <div className="dash-section dash-pie-card">
          <h3 className="dash-section-title" style={{ margin: '0 0 12px 0' }}>Requests by Service</h3>
          <div className="dash-pie-chart-wrap">
            <svg viewBox="-80 -80 160 160" className="dash-pie-svg">
              {pieSlices.map((slice, i) => (
                <path key={i} d={slice.d}
                  fill={slice.color}
                  opacity={pieHover === null || pieHover === i ? 1 : 0.35}
                  stroke="var(--bg-primary)" strokeWidth="1.5"
                  onMouseEnter={() => setPieHover(i)}
                  onMouseLeave={() => setPieHover(null)}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s, transform 0.2s',
                    transform: pieHover === i ? `translate(${Math.cos(slice.midAngle) * 4}px, ${Math.sin(slice.midAngle) * 4}px)` : 'none' }}
                />
              ))}
              {pieHover !== null && pieSlices[pieHover] && (
                <>
                  <text x="0" y="-6" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="600">
                    {pieSlices[pieHover].pct}%
                  </text>
                  <text x="0" y="10" textAnchor="middle" fill="var(--text-muted)" fontSize="9">
                    {fmtVal(pieSlices[pieHover].count)}
                  </text>
                </>
              )}
              {pieHover === null && (
                <text x="0" y="4" textAnchor="middle" fill="var(--text-muted)" fontSize="9">
                  {fmtVal(totalRequests)} total
                </text>
              )}
            </svg>
          </div>
          <div className="dash-pie-legend">
            {serviceEntries.map((entry, i) => (
              <div key={entry.name}
                className={`dash-pie-legend-item ${pieHover === i ? 'active' : ''}`}
                onMouseEnter={() => setPieHover(i)}
                onMouseLeave={() => setPieHover(null)}>
                <span className="dash-pie-dot" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                <span className="dash-pie-svc-name">{entry.name}</span>
                <span className="dash-pie-svc-count">{fmtVal(entry.count)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DashErrorRow({ err, message, count, latest }) {
  const [expanded, setExpanded] = useState(false);
  const shortMessage = message.length > 200 ? message.slice(0, 200) + '...' : message;

  const hasResourceLabels = err.resource?.labels && Object.keys(err.resource.labels).length > 0;
  const hasLabels = err.labels && Object.keys(err.labels).length > 0;
  const hasHttp = err.httpRequest && (err.httpRequest.requestMethod || err.httpRequest.requestUrl);
  const hasOperation = err.operation && (err.operation.id || err.operation.producer);
  const hasSource = err.sourceLocation && (err.sourceLocation.file || err.sourceLocation.function);

  return (
    <div className={`dash-error-row ${err.severity === 'CRITICAL' ? 'critical' : 'error'} ${expanded ? 'expanded' : ''}`}>
      <div className="dash-error-row-main" onClick={() => setExpanded(!expanded)}>
        <div className="dash-error-chevron">
          <span className={`log-chevron ${expanded ? 'open' : ''}`}>&#9654;</span>
        </div>
        <div className="dash-error-severity">
          <span className={`log-severity-badge ${err.severity === 'CRITICAL' ? 'severity-critical' : 'severity-error'}`}>
            {err.severity}
          </span>
        </div>
        <div className="dash-error-time">{new Date(latest || err.timestamp).toLocaleString()}</div>
        <div className="dash-error-service">
          {err.resource?.labels?.service_name || '-'}
          {count > 1 && <span className="dash-error-count">×{count}</span>}
        </div>
        <div className="dash-error-message">{expanded ? message : shortMessage}</div>
      </div>
      {expanded && (
        <div className="dash-error-expanded" onClick={e => e.stopPropagation()}>
          {count > 1 && (
            <div className="dash-error-occurrence-note">
              This error occurred <strong>{count} times</strong> in the last 24 hours
            </div>
          )}
          {err.jsonPayload && (
            <div className="log-exp-section">
              <div className="log-exp-title">JSON Payload</div>
              <div className="log-json-view"><pre>{JSON.stringify(err.jsonPayload, null, 2)}</pre></div>
            </div>
          )}
          {err.textPayload && (
            <div className="log-exp-section">
              <div className="log-exp-title">Text Payload</div>
              <div className="log-text-view" style={{ whiteSpace: 'pre-wrap' }}>{err.textPayload}</div>
            </div>
          )}
          <div className="log-exp-section">
            <div className="log-exp-title">Log Entry</div>
            <div className="log-kv-table">
              <LogKV label="Insert ID" value={err.insertId} />
              <LogKV label="Log Name" value={err.logName} />
              <LogKV label="Severity" value={err.severity} />
              <LogKV label="Timestamp" value={safeISOString(err.timestamp)} />
              <LogKV label="Receive Timestamp" value={safeISOString(err.receiveTimestamp)} />
              <LogKV label="Trace" value={err.trace} />
              <LogKV label="Span ID" value={err.spanId} />
            </div>
          </div>
          {err.resource && (
            <div className="log-exp-section">
              <div className="log-exp-title">Resource</div>
              <div className="log-kv-table">
                <LogKV label="Type" value={err.resource.type} />
                {hasResourceLabels && Object.entries(err.resource.labels).map(([k, v]) => <LogKV key={k} label={k} value={v} />)}
              </div>
            </div>
          )}
          {hasLabels && (
            <div className="log-exp-section">
              <div className="log-exp-title">Labels</div>
              <div className="log-kv-table">
                {Object.entries(err.labels).map(([k, v]) => <LogKV key={k} label={k} value={v} />)}
              </div>
            </div>
          )}
          {hasHttp && (
            <div className="log-exp-section">
              <div className="log-exp-title">HTTP Request</div>
              <div className="log-kv-table">
                <LogKV label="Method" value={err.httpRequest.requestMethod} />
                <LogKV label="URL" value={err.httpRequest.requestUrl} />
                <LogKV label="Status" value={err.httpRequest.status} />
                <LogKV label="Latency" value={err.httpRequest.latency} />
              </div>
            </div>
          )}
          {hasOperation && (
            <div className="log-exp-section">
              <div className="log-exp-title">Operation</div>
              <div className="log-kv-table">
                <LogKV label="ID" value={err.operation.id} />
                <LogKV label="Producer" value={err.operation.producer} />
              </div>
            </div>
          )}
          {hasSource && (
            <div className="log-exp-section">
              <div className="log-exp-title">Source Location</div>
              <div className="log-kv-table">
                <LogKV label="File" value={err.sourceLocation.file} />
                <LogKV label="Line" value={err.sourceLocation.line} />
                <LogKV label="Function" value={err.sourceLocation.function} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DashboardTab;
